import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import PageLayout from '@/components/layout/PageLayout';
import { format } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { ArrowUpDown, Calendar, Check, ChevronDown, ChevronUp, ChevronsLeft, ChevronsRight, UserCheck, UserMinus, UserPlus, UserX, X } from "lucide-react";
import { Database } from '@/integrations/supabase/types';
import { DirectionProvider } from '@radix-ui/react-direction';
import { completeUserDeletion } from '@/utils/supabase-helpers';
import { logUserApproval, logUserRejection, logPermissionChange, logUserDeletion, logUserSuspension } from '@/utils/action-logger';
import EventPlanner from './EventPlanner';
import { SectionHeading } from '@/components/ui/section-heading';
import AdminMediaManager from "./AdminMediaManager";
import AdminMerchandise from "./AdminMerchandise";

interface ProfileWithEmail {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  role: string;
  is_approved: boolean;
  suspended: boolean;
  created_at: string;
  updated_at: string;
}

type UserSortKey = 'first_name' | 'last_name' | 'email' | 'created_at' | 'suspended';
type SortDirection = 'asc' | 'desc';

const adminSupabase = supabase as any;

const PROTECTED_ADMIN_EMAILS = ['josephrose@hotmail.com', 'stuartw4812@gmail.com', 'juliet4williams@outlook.com', 'martialartscolumbus@gmail.com'];

export default function AdminArea() {
  const { user } = useAuth();
  const [users, setUsers] = useState<ProfileWithEmail[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("awaiting-approval");
  const [userSort, setUserSort] = useState<{ key: UserSortKey; direction: SortDirection } | null>(null);
  const mobileTabsScrollRef = useRef<HTMLDivElement | null>(null);
  const [showMobileTabsLeftHint, setShowMobileTabsLeftHint] = useState(false);
  const [showMobileTabsRightHint, setShowMobileTabsRightHint] = useState(false);
  const adminTabs = [
    { value: "awaiting-approval", label: "Awaiting Approval", mobileLabel: "Approval" },
    { value: "user-management", label: "User Management", mobileLabel: "Users" },
    { value: "event-planner", label: "Event Planner", mobileLabel: "Events" },
    { value: "media-library", label: "Media Library", mobileLabel: "Media" },
    { value: "merchandise", label: "Merchandise", mobileLabel: "Merch" },
  ] as const;

  const updateMobileTabOverflowHints = useCallback(() => {
    const container = mobileTabsScrollRef.current;
    if (!container) return;

    const { scrollLeft, clientWidth, scrollWidth } = container;
    const maxScrollLeft = scrollWidth - clientWidth;

    setShowMobileTabsLeftHint(scrollLeft > 4);
    setShowMobileTabsRightHint(maxScrollLeft - scrollLeft > 4);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    updateMobileTabOverflowHints();

    const handleResize = () => updateMobileTabOverflowHints();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [activeTab, updateMobileTabOverflowHints]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .rpc('get_all_profiles_with_email');

      if (error) {
        throw error;
      }
      
      if (data) {
        setUsers(data as ProfileWithEmail[]);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load users",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateUserRole = async (userId: string, newRole: string) => {
    try {
      if (!user) return;
      setIsUpdating(userId);
      
      const targetUser = users.find(u => u.id === userId);
      if (!targetUser) throw new Error('User not found');
      
      if (PROTECTED_ADMIN_EMAILS.includes(targetUser.email)) {
        toast({
          variant: "destructive",
          title: "Permission Denied",
          description: "Cannot change role for protected administrator account",
        });
        return;
      }
      
      const { error } = await adminSupabase.rpc('admin_update_user_role', {
        p_user_id: userId,
        p_role: newRole
      });

      if (error) throw error;
      
      // Log permission change with admin user ID
      await logPermissionChange(userId, newRole, user.id);
      
      toast({
        title: "Success",
        description: `User role updated to ${newRole}`,
      });
      setUsers(users.map(u => 
        u.id === userId ? { ...u, role: newRole } : u
      ));
    } catch (error) {
      console.error('Error updating user role:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update user role",
      });
    } finally {
      setIsUpdating(null);
    }
  };

  const sendApprovalEmail = async (email: string, firstName: string, lastName: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('send-approval-email', {
        body: {
          email,
          firstName,
          lastName
        }
      });

      if (error) {
        console.error('Error sending approval email:', error);
        throw error;
      }

      console.log('Approval email sent successfully:', data);
    } catch (error) {
      console.error('Failed to send approval email:', error);
      toast({
        variant: "destructive",
        title: "Email Error",
        description: "Failed to send approval notification email",
      });
    }
  };

  const updateUserApproval = async (userId: string, isApproved: boolean) => {
    try {
      if (!user) return;
      setIsUpdating(userId);
      
      const targetUser = users.find(u => u.id === userId);
      if (!targetUser) throw new Error('User not found');
      
      if (PROTECTED_ADMIN_EMAILS.includes(targetUser.email)) {
        toast({
          variant: "destructive",
          title: "Permission Denied",
          description: "Cannot change approval status for protected administrator account",
        });
        return;
      }
      
      const { error } = await adminSupabase.rpc('admin_update_user_approval', {
        p_user_id: userId,
        p_is_approved: isApproved
      });

      if (error) throw error;
      
      if (isApproved) {
        await sendApprovalEmail(
          targetUser.email,
          targetUser.first_name || 'OMAC Member',
          targetUser.last_name || ''
        );
        // Log user approval with admin user ID
        await logUserApproval(userId, user.id);
      } else {
        // Log user rejection with admin user ID
        await logUserRejection(userId, user.id);
      }
      
      toast({
        title: "Success",
        description: `User ${isApproved ? 'approved' : 'unapproved'}`,
      });
      setUsers(users.map(u => 
        u.id === userId ? { ...u, is_approved: isApproved } : u
      ));
    } catch (error) {
      console.error('Error updating user approval status:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update approval status",
      });
    } finally {
      setIsUpdating(null);
    }
  };

  const updateUserSuspension = async (userId: string, isSuspended: boolean) => {
    try {
      if (!user) return;
      setIsUpdating(userId);
      
      const targetUser = users.find(u => u.id === userId);
      if (!targetUser) throw new Error('User not found');
      
      if (PROTECTED_ADMIN_EMAILS.includes(targetUser.email)) {
        toast({
          variant: "destructive",
          title: "Permission Denied",
          description: "Cannot change suspension status for protected administrator account",
        });
        return;
      }
      
      const { error } = await adminSupabase.rpc('admin_update_user_suspension', {
        p_user_id: userId,
        p_suspended: isSuspended
      });

      if (error) throw error;
      
      // Log user suspension status change with admin user ID
      await logUserSuspension(userId, isSuspended, user.id);
      
      toast({
        title: "Success",
        description: `User ${isSuspended ? 'suspended' : 'reinstated'}`,
      });
      setUsers(users.map(u => 
        u.id === userId ? { ...u, suspended: isSuspended } : u
      ));
    } catch (error) {
      console.error('Error updating user suspension status:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update suspension status",
      });
    } finally {
      setIsUpdating(null);
    }
  };

  const deleteUser = async (userId: string, userEmail: string) => {
    if (!user) return;
    
    if (PROTECTED_ADMIN_EMAILS.includes(userEmail)) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Cannot delete protected administrator account",
      });
      return;
    }

    try {
      setIsDeleting(userId);
      
      console.log(`Deleting user with ID: ${userId}`);
      
      await completeUserDeletion(userId);
      
      // Log user deletion with admin user ID
      await logUserDeletion(userId, user.id);
      
      toast({
        title: "Success",
        description: "User deleted successfully",
      });
      setUsers(users.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete user. Please try again.",
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const awaitingApprovalUsers = users.filter(user => !user.is_approved);
  const approvedUsers = users.filter(user => user.is_approved);
  
  const administrators = approvedUsers.filter(user => user.role === 'administrator');
  const customers = approvedUsers.filter(user => user.role === 'customer');

  const normalizeText = (value: string | null) => value?.trim().toLowerCase() ?? '';

  const getSortedUsers = (userList: ProfileWithEmail[]) => {
    if (!userSort) return userList;

    const sortedUsers = [...userList].sort((a, b) => {
      switch (userSort.key) {
        case 'first_name':
          return normalizeText(a.first_name).localeCompare(normalizeText(b.first_name));
        case 'last_name':
          return normalizeText(a.last_name).localeCompare(normalizeText(b.last_name));
        case 'email':
          return a.email.localeCompare(b.email, undefined, { sensitivity: 'base' });
        case 'created_at':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'suspended':
          return (a.suspended ? 'yes' : 'no').localeCompare(b.suspended ? 'yes' : 'no');
        default:
          return 0;
      }
    });

    return userSort.direction === 'asc' ? sortedUsers : sortedUsers.reverse();
  };

  const toggleUserSort = (key: UserSortKey) => {
    setUserSort((currentSort) => {
      if (!currentSort || currentSort.key !== key) {
        return { key, direction: 'asc' };
      }

      return {
        key,
        direction: currentSort.direction === 'asc' ? 'desc' : 'asc'
      };
    });
  };

  const renderSortIcon = (key: UserSortKey) => {
    if (userSort?.key !== key) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }

    return userSort.direction === 'asc'
      ? <ChevronUp className="ml-2 h-4 w-4" />
      : <ChevronDown className="ml-2 h-4 w-4" />;
  };

  const renderSortableHeader = (label: string, key: UserSortKey) => (
    <TableHead className="bg-gray-100 text-taekwondo-red">
      <button
        type="button"
        onClick={() => toggleUserSort(key)}
        className="inline-flex items-center font-semibold transition-colors hover:text-taekwondo-blue"
      >
        {label}
        {renderSortIcon(key)}
      </button>
    </TableHead>
  );

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
    } catch (e) {
      return 'Invalid date';
    }
  };

  const handleRoleChange = (userId: string, currentRole: string, newRole: string) => {
    if (newRole === 'administrator' && currentRole === 'customer') {
      const confirmPromotion = window.confirm('Are you sure you want to promote this user to administrator?');
      if (confirmPromotion) {
        updateUserRole(userId, newRole);
      }
    } else {
      updateUserRole(userId, newRole);
    }
  };

  const handleApprovalAction = (userId: string, userEmail: string, action: 'approve' | 'reject') => {
    if (action === 'approve') {
      updateUserApproval(userId, true);
    } else if (action === 'reject') {
      const confirmRejection = window.confirm('Are you sure you want to reject and delete this user? This cannot be undone.');
      if (confirmRejection) {
        deleteUser(userId, userEmail);
      }
    }
  };

  const renderAwaitingApprovalTable = () => (
    <div className="w-full px-4">
           
      <h2 className="text-2xl font-semibold mb-6 mt-6 text-taekwondo-red">Users Awaiting Approval</h2>
      {awaitingApprovalUsers.length === 0 ? (
        <div className="bg-gray-50 rounded p-6 text-center">
          <p className="text-gray-500">No users awaiting approval.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="bg-gray-100 text-taekwondo-red">First Name</TableHead>
                <TableHead className="bg-gray-100 text-taekwondo-red">Last Name</TableHead>
                <TableHead className="bg-gray-100 text-taekwondo-red">Email</TableHead>
                <TableHead className="bg-gray-100 text-taekwondo-red">Date Requested</TableHead>
                <TableHead className="bg-gray-100 text-taekwondo-red">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {awaitingApprovalUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.first_name || 'N/A'}</TableCell>
                  <TableCell>{user.last_name || 'N/A'}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{formatDate(user.created_at)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="default" size="sm" className="bg-taekwondo-blue hover:bg-taekwondo-blue/90 text-white">
                          Actions
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={() => handleApprovalAction(user.id, user.email, 'approve')}
                          className="cursor-pointer"
                          disabled={isUpdating === user.id}
                        >
                          <UserCheck className="mr-2 h-4 w-4" /> Approve
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleApprovalAction(user.id, user.email, 'reject')}
                          className="cursor-pointer text-red-600"
                          disabled={isDeleting === user.id || user.email === 'josephrose@hotmail.com'}
                        >
                          {isDeleting === user.id ? (
                            'Deleting...'
                          ) : (
                            <><X className="mr-2 h-4 w-4" /> Reject</>
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );

  const renderUserTable = (userList: ProfileWithEmail[], title: string) => (
    <div className="w-full px-4">
      <h2 className="text-2xl font-semibold mb-6 mt-6 text-taekwondo-red">{title}</h2>
      {userList.length === 0 ? (
        <div className="bg-gray-50 rounded p-6 text-center">
          <p className="text-gray-500">No {title.toLowerCase()} found.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {renderSortableHeader('First Name', 'first_name')}
                {renderSortableHeader('Last Name', 'last_name')}
                {renderSortableHeader('Email', 'email')}
                <TableHead className="bg-gray-100 text-taekwondo-red">Role</TableHead>
                {renderSortableHeader('Joined', 'created_at')}
                {renderSortableHeader('Suspended', 'suspended')}
                <TableHead className="bg-gray-100 text-taekwondo-red">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {getSortedUsers(userList).map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.first_name || 'N/A'}</TableCell>
                  <TableCell>{user.last_name || 'N/A'}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Select 
                      value={user.role} 
                      onValueChange={(value: string) => 
                        handleRoleChange(user.id, user.role, value)
                      }
                      disabled={isUpdating === user.id || PROTECTED_ADMIN_EMAILS.includes(user.email)}
                    >
                      <SelectTrigger className="w-[130px]">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="customer">Customer</SelectItem>
                        <SelectItem value="administrator">Administrator</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>{formatDate(user.created_at)}</TableCell>
                  <TableCell>
                    <Select 
                      value={user.suspended ? 'yes' : 'no'} 
                      onValueChange={(value) => 
                        updateUserSuspension(user.id, value === 'yes')
                      }
                      disabled={isUpdating === user.id || PROTECTED_ADMIN_EMAILS.includes(user.email)}
                    >
                      <SelectTrigger className="w-[80px]">
                        <SelectValue placeholder="Suspended?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="default" 
                          size="sm" 
                          className="bg-taekwondo-blue hover:bg-taekwondo-blue/90 text-white" 
                          disabled={PROTECTED_ADMIN_EMAILS.includes(user.email)}
                        >
                          Actions
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={() => updateUserSuspension(user.id, !user.suspended)}
                          className="cursor-pointer"
                          disabled={PROTECTED_ADMIN_EMAILS.includes(user.email)}
                        >
                          {user.suspended ? (
                            <><UserPlus className="mr-2 h-4 w-4" /> Reinstate</>
                          ) : (
                            <><UserMinus className="mr-2 h-4 w-4" /> Suspend</>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => deleteUser(user.id, user.email)}
                          className="cursor-pointer text-red-600"
                          disabled={PROTECTED_ADMIN_EMAILS.includes(user.email) || isDeleting === user.id}
                        >
                          {isDeleting === user.id ? (
                            'Deleting...'
                          ) : (
                            <><X className="mr-2 h-4 w-4" /> Delete</>
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );

  return (
    <PageLayout>
      <div className="container mx-auto py-16 px-4">
        <SectionHeading        
          title="Admin Area"
          gradient={true}
        />
        
        <div className="bg-white shadow-md rounded-lg">
          <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="md:hidden relative border-b bg-white/95 backdrop-blur">
              <div
                ref={mobileTabsScrollRef}
                onScroll={updateMobileTabOverflowHints}
                className="touch-pan-x overflow-x-auto px-2 py-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
              >
                <TabsList className="inline-flex h-auto min-w-full justify-start gap-2 rounded-full bg-gray-100 p-1">
                  {adminTabs.map((tab) => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      className="whitespace-nowrap rounded-full px-4 py-2 text-sm"
                    >
                      {tab.mobileLabel}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
              {showMobileTabsLeftHint && (
                <div className="pointer-events-none absolute inset-y-0 left-0 flex w-16 items-center justify-start bg-gradient-to-r from-white via-white/90 to-transparent pl-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full border border-taekwondo-red/20 bg-white/95 shadow-md shadow-taekwondo-red/10 ring-1 ring-white/70">
                    <ChevronsLeft className="h-6 w-6 text-taekwondo-red" />
                  </div>
                </div>
              )}
              {showMobileTabsRightHint && (
                <div className="pointer-events-none absolute inset-y-0 right-0 flex w-16 items-center justify-end bg-gradient-to-l from-white via-white/90 to-transparent pr-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full border border-taekwondo-red/20 bg-white/95 shadow-md shadow-taekwondo-red/10 ring-1 ring-white/70">
                    <ChevronsRight className="h-6 w-6 text-taekwondo-red" />
                  </div>
                </div>
              )}
            </div>

            {/* Desktop Navigation Tabs */}
            <div className="hidden md:block">
              <TabsList className="mb-6">
                {adminTabs.map((tab) => (
                  <TabsTrigger key={tab.value} value={tab.value}>
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <TabsContent value="awaiting-approval">
              {isLoading ? (
                <p>Loading users...</p>
              ) : (
                renderAwaitingApprovalTable()
              )}
            </TabsContent>
            
            <TabsContent value="user-management">
              {isLoading ? (
                <p>Loading users...</p>
              ) : (
                <>
                  {renderUserTable(administrators, "Administrators")}
                  {renderUserTable(customers, "Customers")}
                </>
              )}
            </TabsContent>

            <TabsContent value="event-planner">
              <EventPlanner />
            </TabsContent>

            <TabsContent value="media-library">
              <AdminMediaManager />
            </TabsContent>

            <TabsContent value="merchandise">
              <AdminMerchandise />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </PageLayout>
  );
}
